import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Calendar, ChevronLeft, ChevronRight, Clock, User, MapPin } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

function ClientCalendarView({ db, client }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // 'month' or 'week'
  const { theme } = useTheme();

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      // Récupérer tous les employés sans filtre isArchived côté Firestore
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080'
      }));
      
      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const filteredEmployees = fetchedEmployees.filter(employee => {
        // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
        if (employee.role === 'beneficiaire') {
          return false;
        }
        
        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;
        
        return isNotArchived && isActive;
      });
      
      console.log('🔍 Employés récupérés dans ClientMonthCalendar:', {
        total: fetchedEmployees.length,
        filtrés: filteredEmployees.length,
        détails: fetchedEmployees.map(emp => ({
          id: emp.userId,
          nom: emp.name || `${emp.firstName} ${emp.lastName}`,
          isArchived: emp.isArchived,
          isActive: emp.isActive,
          role: emp.role
        }))
      });
      
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db]);

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    if (employee) {
      return employee.name || `${employee.firstName} ${employee.lastName}`;
    } else {
      console.warn(`Employé non trouvé pour l'ID: ${employeeId}`);
      return "Employé non trouvé";
    }
  };

  const getEmployeeColor = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee?.color || '#808080'; // Couleur par défaut si non trouvée
  };

  useEffect(() => {
    fetchEmployees();
    loadAppointments();
  }, [client, currentDate, fetchEmployees]);

  const loadAppointments = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef, 
        where('clientId', '==', client.id),
        where('date', '>=', startOfMonth),
        where('date', '<=', endOfMonth)
      );
      
      const querySnapshot = await getDocs(q);
      const appointmentsData = [];
      
      querySnapshot.forEach((doc) => {
        const appointment = {
          id: doc.id,
          ...doc.data()
        };
        appointmentsData.push(appointment);
      });

      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const formatTime = (time) => {
    return time.substring(0, 5); // Format HH:MM
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];

    // Ajouter les jours du mois précédent pour compléter la première semaine
    const firstDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    for (let i = 0; i < firstDayOfWeek; i++) {
      const prevDate = new Date(firstDay);
      prevDate.setDate(prevDate.getDate() - (firstDayOfWeek - i));
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    // Ajouter les jours du mois courant
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      days.push({ date, isCurrentMonth: true });
    }

    // Ajouter les jours du mois suivant pour compléter la dernière semaine
    const lastDayOfWeek = lastDay.getDay() === 0 ? 0 : 7 - lastDay.getDay();
    for (let i = 1; i <= lastDayOfWeek; i++) {
      const nextDate = new Date(lastDay);
      nextDate.setDate(nextDate.getDate() + i);
      days.push({ date: nextDate, isCurrentMonth: false });
    }

    return days;
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(app => {
      const appDate = app.date.toDate();
      return appDate.toDateString() === date.toDateString();
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  const monthName = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth();
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="mr-3 text-blue-600 dark:text-blue-400" size={24} />
          Planning du mois
        </h2>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronLeft size={20} />
            </button>

            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {monthName}
            </span>

            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          
          
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {weekDays.map(day => (
          <div key={day} className="text-center font-medium text-gray-700 dark:text-gray-300 py-2">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days.map((day, index) => {
          const dayAppointments = getAppointmentsForDate(day.date);
          const isToday = day.date.toDateString() === new Date().toDateString();
          
          return (
            <div
              key={index}
              className={`min-h-24 p-2 border rounded-lg ${
                day.isCurrentMonth
                  ? isToday
                    ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
                  : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
              }`}
            >
              <div className={`text-sm font-medium mb-2 ${
                day.isCurrentMonth
                  ? isToday
                    ? 'text-blue-700 dark:text-blue-400'
                    : 'text-gray-900 dark:text-white'
                  : 'text-gray-400 dark:text-gray-500'
              }`}>
                {day.date.getDate()}
              </div>
              
              <div className="space-y-1">
                {dayAppointments.slice(0, 3).map(appointment => {
                  const employeeColor = appointment.employeeId ? getEmployeeColor(appointment.employeeId) : '#808080';
                  return (
                    <div
                      key={appointment.id}
                      className="rounded-lg px-2 py-1.5 text-xs border-l-3 hover:shadow-sm transition-all duration-200 bg-white dark:bg-gray-600"
                      style={{
                        borderLeftColor: employeeColor
                      }}
                    >
                      <div className="font-bold text-gray-900 dark:text-white mb-0.5">
                        {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                      </div>
                      <div className="flex items-center text-gray-700 dark:text-gray-300">
                        <div
                          className="w-3 h-3 rounded-full mr-1.5"
                          style={{ backgroundColor: employeeColor }}
                        ></div>
                        <span className="text-[10px] truncate">
                          {appointment.employeeId ? getEmployeeName(appointment.employeeId) : 'Non assigné'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                
                {dayAppointments.length > 3 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    +{dayAppointments.length - 3} autres
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Appointments List */}
      {appointments.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Tous vos rendez-vous ({appointments.length})
          </h3>

          <div className="space-y-3">
            {appointments.map(appointment => (
              <div key={appointment.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <Calendar className="text-blue-600 dark:text-blue-400 mr-2" size={16} />
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatDate(appointment.date.toDate())}
                      </span>
                    </div>

                    <div className="flex items-center mb-1">
                      <Clock className="text-gray-600 dark:text-gray-400 mr-2" size={16} />
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                      </span>
                    </div>

                    <div className="flex items-center mb-1">
                      <User className="text-gray-600 dark:text-gray-400 mr-2" size={16} />
                      <span className="text-gray-700 dark:text-gray-300">
                        {appointment.employeeId ? getEmployeeName(appointment.employeeId) : 'Intervenant non spécifié'}
                      </span>
                    </div>

                    <div className="flex items-center">
                      <MapPin className="text-gray-600 dark:text-gray-400 mr-2" size={16} />
                      <span className="text-gray-700 dark:text-gray-300">{appointment.serviceType}</span>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Statut</div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      appointment.status === 'confirmed'
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : appointment.status === 'cancelled'
                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                    }`}>
                      {appointment.status === 'confirmed' ? 'Confirmé' :
                       appointment.status === 'cancelled' ? 'Annulé' : 'En attente'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientCalendarView;
